/**
 * @file        src/pages/flight/CouponList.jsx
 * @description Coupon List (CRUD) over coupon_codes, shaped to the legacy
 *              console's Coupon Management: Amount, Amount Type (Percent/Fixed),
 *              Coupon (auto-generated code), Remark, Start/Expiry Date, Use Limit.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (B2C Flight Management).
 *   - 2026-07-08 Claude — Legacy parity: Amount/Amount Type/Coupon/Remark/Start/
 *                          Expiry/Use Limit, date pickers, and an auto-generated
 *                          coupon code pre-filled on "Generate Coupon".
 *   - 2026-08-03 Claude — Required Brand field (registry code, no default) —
 *                          the API now demands `brand` on create.
 *   - 2026-08-10 Claude — Expiry Date can no longer be a past date, and can no
 *                          longer precede Start Date. Console-wide date rule
 *                          pass (src/lib/dateRules.js), mirrored server-side.
 *   - 2026-08-10 Claude — Added the `applicable_role` field (All/B2C/B2B — the
 *                          column existed since migration 026 but no admin
 *                          screen exposed it) and title/subtitle/audience props
 *                          so CorporateCouponList.jsx can reuse this page fixed
 *                          to audience=b2b instead of forking it.
 *   - 2026-09-12 Claude — Stopped showing "Applies To": the screen decides it
 *                          (this page b2c, CorporateCouponList.jsx b2b). It
 *                          defaulted to 'all', so every retail promo code was
 *                          silently redeemable by corporate buyers — the same
 *                          B2C→B2B bleed removed from markup the same day.
 *   - 2026-08-10 Claude — Status column now shows the LIVE redemption status
 *                          (couponStatus, via SimpleLookupPage's new `statusOf`)
 *                          instead of just the `is_active` toggle, so a lapsed
 *                          coupon reads as "Expired" here instead of "Active"
 *                          while it's already dead on the storefront.
 *   - 2026-09-15 Claude — Added the read-only "Used" column. The list showed the
 *                          Use Limit but never the count against it, so the only
 *                          signal that a coupon was spent was the Status chip —
 *                          and that chip read "Active" for a spent coupon
 *                          whenever `used_count` had failed to keep up (it was
 *                          only ever incremented by the payment callback, which
 *                          a late-ticketed flight never reaches). Inherited by
 *                          CorporateCouponList.jsx.
 *   - 2026-09-15 Claude — Amount is now bounded on BOTH sides by the shared rule
 *                          (lib/discountRules.js). The field allowed "0 or
 *                          more", so a 0-value coupon saved happily — it
 *                          validates on the storefront, burns one of its uses
 *                          and takes nothing off — and a Percent coupon had no
 *                          ceiling, so "500" saved as 500% and was then silently
 *                          clamped to the fare at checkout. Now > 0 always, and
 *                          <= 100 when Amount Type is Percent. Inherited by
 *                          CorporateCouponList.jsx, which reuses this page.
 *   - 2026-09-15 Claude — Added the one-line note stating HOW a coupon reaches
 *                          the bill: only when the customer types the code, on
 *                          Fare + Taxes + Markup + Add-ons + Convenience fee,
 *                          stacking on top of the automatic discount. The screen
 *                          showed the rules but never the base they apply to.
 *   - 2026-10-05 Claude — Airline Type / Airline Code / Fare Type, the three
 *                          scope fields the Flight Markup screen has (migration
 *                          074): a coupon can be limited to one airline and/or
 *                          one fare family. Shared definitions in lib/fareTypes.
 *   - 2026-10-06 Claude — Domestic / International (migration 076). A refused
 *                          code tells the customer which trips it is for.
 */
import SimpleLookupPage from '../package/SimpleLookupPage';
import { todayStr, laterDate, pastDateError, dateRangeError } from '../../lib/dateRules';
import { couponStatus } from '../../lib/couponStatus';
import { discountValueError, discountValueMax } from '../../lib/discountRules';
import { FLIGHT_COUPON_NOTE } from '../../lib/pricingNotes';
import { flightScopeFields, flightScopeError } from '../../lib/fareTypes';
import { domIntField } from '../../lib/domInt';

const AMOUNT_TYPE = [
  { value: 'percentage', label: 'Percent' },
  { value: 'fixed', label: 'Fixed' },
];
// Label map for the read-only "Applies To" column (the field is `form: false` —
// the SCREEN decides the audience, it is never picked in the modal). 'all' is
// retired (admin migration 055) and survives here only so a row on a tenant DB
// that has not been migrated yet reads as the legacy wildcard instead of raw text.
const ROLE = [
  { value: 'b2c', label: 'B2C (retail)' },
  { value: 'b2b', label: 'B2B / Corporate' },
  { value: 'all', label: 'All (legacy)' },
];

// Fresh 8-char A–Z/0–9 code each time the Add form opens (like the old console).
function genCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let s = '';
  for (let i = 0; i < 8; i += 1) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

export default function CouponList({
  title = 'Coupon List',
  subtitle = 'B2C Flight · discount coupons (redeemable on flights only)',
  audience = 'b2c',
} = {}) {
  return (
    <SimpleLookupPage
      title={title}
      subtitle={subtitle}
      endpoint="/coupons"
      queryRoot={`coupons-flight-${audience}`}
      notice={FLIGHT_COUPON_NOTE}
      addLabel="Generate Coupon"
      entityLabel="Coupon"
      // Scope: this page lists and creates FLIGHT coupons. A coupon created here
      // cannot be redeemed on a hotel or an insurance policy.
      listParams={{ applicable_for: 'flight', audience }}
      fixedValues={{ applicable_for: 'flight' }}
      fields={[
        { key: 'brand', label: 'Brand', type: 'brand', required: true },
        // Ceiling tracks Amount Type: 100 for Percent, none for Fixed (rupees).
        { key: 'discount_value', label: 'Amount', type: 'number', required: true, min: 0, step: '0.01', max: (form) => discountValueMax(form.discount_type) },
        { key: 'discount_type', label: 'Amount Type', type: 'select', required: true, default: 'fixed', options: AMOUNT_TYPE },
        { key: 'code', label: 'Coupon', type: 'text', required: true, default: genCode, maxLength: 40 },
        // Which airline / fare family the code may be redeemed on (blank = any).
        ...flightScopeFields(),
        // Domestic / International trips (blank = both).
        domIntField(),
        { key: 'remark', label: 'Remark', type: 'text', maxLength: 255 },
        { key: 'valid_from', label: 'Start Date', type: 'date' },
        { key: 'valid_to', label: 'Expiry Date', type: 'date', min: (form) => laterDate(todayStr(), form.valid_from) },
        { key: 'usage_limit', label: 'Use Limit', type: 'number', min: 0, placeholder: '0 = unlimited' },
        // Read-only (`form: false`): how many of that limit are already spent.
        // Without it the Status chip was the ONLY hint that a coupon was used
        // up, so "Use Limit 1" beside an "Active" chip told an admin nothing
        // about whether the code was still spendable. The Hotel Coupon List has
        // shown this since it was built. The column is DERIVED server-side
        // (couponCodeModel.syncUsedCount) — never an editable field.
        { key: 'used_count', label: 'Used', type: 'number', form: false },
        // Fixed by the screen, not chosen: this page makes retail coupons and
        // CorporateCouponList.jsx makes corporate ones. `form: false` keeps the
        // table column (so an older 'All' row still reads as one) but drops the
        // input — a corporate promo filed from the retail screen was invisible
        // on the screen that created it.
        { key: 'applicable_role', label: 'Applies To', type: 'select', options: ROLE, form: false, default: audience === 'b2b' ? 'b2b' : 'b2c' },
      ]}
      validate={(form) => {
        const errs = { ...flightScopeError(form) };
        const amountErr = discountValueError(form.discount_type, form.discount_value);
        if (amountErr) errs.discount_value = amountErr;
        const err = pastDateError(form.valid_to, 'Expiry Date') || dateRangeError(form.valid_from, form.valid_to, 'Start Date', 'Expiry Date');
        if (err) errs.valid_to = err;
        return errs;
      }}
      statusOf={couponStatus}
    />
  );
}
